import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, XCircle, Globe, Instagram, Video,
  MapPin, Users, Calendar, RefreshCw, Loader2, Linkedin, Pencil, X,
  Sparkles, ChevronDown, ChevronRight, RotateCcw, AlertTriangle,
  CircleDashed, FileText,
} from 'lucide-react';
import { api } from '../utils/api';
import OrganicShell from '../components/organic/OrganicShell';

// Review — triage for AI-suggested brands (weekly discovery + CSV
// name-only rows), reached from the badged Review button on Brands.
// Approving runs deep research to fill the missing fields before the
// brand is saved. The polling, timeout and 404→merged handling are
// unchanged from the old Pending Review page; only the presentation
// moved to the Organic shell.

const STROKE = 2.75;

const ENRICHMENT_POLL_MS = 3000;
const ENRICHMENT_POLL_TIMEOUT_MS = 5 * 60 * 1000;
const ENRICHMENT_TERMINAL = new Set([
  'completed', 'failed', 'credits_denied', 'provider_error', 'merged',
]);

const FIELD_LABELS = {
  country: 'Country',
  hq_city: 'HQ city',
  hq_state: 'HQ state',
  founders: 'Founders',
  founded_year: 'Founded year',
  parent_company: 'Parent company',
  instagram_handle: 'Instagram',
  tiktok_handle: 'TikTok',
  linkedin_url: 'LinkedIn',
  youtube_url: 'YouTube',
  marketplaces: 'Marketplaces',
  category: 'Category',
  notes: 'Description',
};

// The fields deep research tries to fill; listed on each card when empty.
const RESEARCHABLE = [
  ['website', 'Website'],
  ['instagram_handle', 'Instagram'],
  ['tiktok_handle', 'TikTok'],
  ['country', 'Country'],
  ['hq_city', 'HQ city'],
  ['founders', 'Founders'],
  ['founded_year', 'Founded year'],
  ['parent_company', 'Parent company'],
];

function describeFilled(filled) {
  if (!Array.isArray(filled) || filled.length === 0) return '';
  const labels = filled.map(c => FIELD_LABELS[c] || c);
  return labels.join(', ');
}

function formatConfidence(score) {
  if (typeof score !== 'number') return null;
  return Math.round(score * 100);
}

function confidenceColor(pct) {
  if (pct >= 85) return 'var(--color-accent-2)';
  if (pct >= 75) return 'var(--color-accent)';
  return 'var(--color-accent-400)';
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function startedAgo(startedAt) {
  if (!startedAt) return '';
  const secs = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
  if (secs < 90) return `started ${secs}s ago`;
  return `started ${Math.round(secs / 60)}m ago`;
}

function PendingReview() {
  const [items, setItems] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [rejectedOpen, setRejectedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useState(null);
  const [editing, setEditing] = useState(null);
  // brandId -> { name, status, filled, message, startedAt }
  const [enrichments, setEnrichments] = useState({});
  // brandId -> intervalId so we can clear when the component unmounts
  // or the brand finishes enriching.
  const pollHandles = useRef({});

  const stopPolling = useCallback((brandId) => {
    const handle = pollHandles.current[brandId];
    if (handle) {
      clearInterval(handle);
      delete pollHandles.current[brandId];
    }
  }, []);

  const updateEnrichment = useCallback((brandId, patch) => {
    setEnrichments(prev => {
      const existing = prev[brandId];
      if (!existing && patch === null) return prev;
      if (patch === null) {
        const { [brandId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [brandId]: { ...(existing || {}), ...patch } };
    });
  }, []);

  const pollOnce = useCallback(async (brandId) => {
    try {
      const brand = await api.getBrand(brandId);
      const status = brand?.enrichment_status || 'completed';
      updateEnrichment(brandId, {
        status,
        filled: Array.isArray(brand?.enrichment_filled) ? brand.enrichment_filled : [],
        message: brand?.enrichment_message || '',
      });
      if (ENRICHMENT_TERMINAL.has(status)) {
        stopPolling(brandId);
      }
    } catch (err) {
      // Network blip: leave the poll loop running, the next tick will
      // retry. Only give up on an explicit 404 — the brand row is gone,
      // which mid-enrichment almost always means the duplicate
      // auto-merge fired: research revealed the brand was already
      // tracked, its data was folded into the existing row and this
      // one was deleted (the full notice is on the surviving brand).
      if (err?.status === 404) {
        stopPolling(brandId);
        updateEnrichment(brandId, {
          status: 'merged',
          message:
            'High-confidence duplicate: this brand was already tracked. '
            + 'The researched data was merged into the existing brand and '
            + 'this row was removed — see the surviving brand on the Brands page.',
        });
      }
    }
  }, [stopPolling, updateEnrichment]);

  const startPolling = useCallback((brandId, name) => {
    updateEnrichment(brandId, {
      name,
      status: 'pending',
      filled: [],
      message: '',
      startedAt: Date.now(),
    });
    // Kick an immediate poll so the banner flips to 'running' as soon
    // as the background task picks up; setInterval otherwise waits a
    // full tick before its first call.
    pollOnce(brandId);
    pollHandles.current[brandId] = setInterval(() => {
      const tracked = enrichmentsRef.current[brandId];
      if (tracked && Date.now() - (tracked.startedAt || 0) > ENRICHMENT_POLL_TIMEOUT_MS) {
        stopPolling(brandId);
        updateEnrichment(brandId, {
          status: 'failed',
          message: 'Enrichment did not finish within 5 minutes — refresh to check the brand page.',
        });
        return;
      }
      pollOnce(brandId);
    }, ENRICHMENT_POLL_MS);
  }, [pollOnce, stopPolling, updateEnrichment]);

  const dismissEnrichment = useCallback((brandId) => {
    stopPolling(brandId);
    updateEnrichment(brandId, null);
  }, [stopPolling, updateEnrichment]);

  // Mirror enrichments into a ref so the setInterval closure can read
  // the latest value without retriggering startPolling on every render.
  const enrichmentsRef = useRef(enrichments);
  useEffect(() => { enrichmentsRef.current = enrichments; }, [enrichments]);

  useEffect(() => () => {
    Object.keys(pollHandles.current).forEach(id => {
      clearInterval(pollHandles.current[id]);
    });
    pollHandles.current = {};
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingData, rejectedData] = await Promise.all([
        api.getPendingBrands('pending'),
        api.getPendingBrands('rejected'),
      ]);
      const sorted = (Array.isArray(pendingData) ? [...pendingData] : []).sort(
        (a, b) => {
          const ac = typeof a.confidence_score === 'number' ? a.confidence_score : -1;
          const bc = typeof b.confidence_score === 'number' ? b.confidence_score : -1;
          if (bc !== ac) return bc - ac;
          // Tiebreaker: most recently discovered first, so equal-confidence
          // items don't shuffle on every refresh.
          return new Date(b.discovered_at || 0) - new Date(a.discovered_at || 0);
        },
      );
      const sortedRejected = (Array.isArray(rejectedData) ? [...rejectedData] : []).sort(
        (a, b) => new Date(b.discovered_at || 0) - new Date(a.discovered_at || 0),
      );
      setItems(sorted);
      setRejected(sortedRejected);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Failed to load pending brands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, name) => {
    setBusyId(id);
    setFlash(null);
    try {
      const result = await api.approvePendingBrand(id);
      const brandId = result?.brand?.id;
      setItems(prev => prev.filter(p => p.id !== id));
      if (result?.duplicate) {
        // Already tracked — no enrichment was queued, nothing to poll.
        setFlash({
          kind: 'ok',
          text: result?.message || `${name} is already tracked.`,
        });
      } else if (brandId) {
        startPolling(brandId, result?.brand?.name || name);
      } else {
        setFlash({
          kind: 'ok',
          text: result?.message || `Approved ${name}.`,
        });
      }
    } catch (err) {
      setFlash({ kind: 'err', text: err?.message || 'Approve failed' });
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id, name) => {
    setBusyId(id);
    setFlash(null);
    const target = items.find(p => p.id === id);
    try {
      await api.rejectPendingBrand(id);
      setFlash({ kind: 'ok', text: `Rejected ${name}. You can restore it from the Rejected section below.` });
      setItems(prev => prev.filter(p => p.id !== id));
      if (target) {
        setRejected(prev => [{ ...target, status: 'rejected' }, ...prev]);
      }
    } catch (err) {
      setFlash({ kind: 'err', text: err?.message || 'Reject failed' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (id, name) => {
    setBusyId(id);
    setFlash(null);
    const target = rejected.find(p => p.id === id);
    try {
      await api.restorePendingBrand(id);
      setFlash({ kind: 'ok', text: `Restored ${name} to pending review.` });
      setRejected(prev => prev.filter(p => p.id !== id));
      if (target) {
        setItems(prev => [{ ...target, status: 'pending' }, ...prev]);
      }
    } catch (err) {
      setFlash({ kind: 'err', text: err?.message || 'Restore failed' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <OrganicShell active="brands" contentStyle={{ maxWidth: 1120 }}>
      <Link to="/brands" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, alignSelf: 'flex-start' }}>
        <ArrowLeft size={15} strokeWidth={STROKE} /> Back to Brands
      </Link>

      <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 340px' }}>
          <h1 className="o-page-title">Review</h1>
          <p className="o-page-subhead" style={{ textWrap: 'pretty' }}>
            Brands found by weekly discovery and by CSV name-only rows. Approving
            runs deep research to fill the missing fields before the brand is saved.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" style={{ flex: 'none' }} onClick={load} disabled={loading}>
          <RefreshCw size={15} strokeWidth={STROKE} />
          <span style={{ whiteSpace: 'nowrap' }}>Refresh</span>
        </button>
      </header>

      {flash && (
        <div
          className="card"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            background: flash.kind === 'ok' ? 'var(--color-accent-2-100)' : 'var(--color-accent-200)',
            fontSize: 14,
          }}
        >
          {flash.text}
        </div>
      )}

      <EnrichmentBanners entries={enrichments} onDismiss={dismissEnrichment} />

      {loading && (
        <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading pending brands…
        </div>
      )}
      {error && (
        <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>{items.length} pending</span>
          <span style={{ opacity: 0.6 }}>sorted by confidence, highest first</span>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="card" style={{ padding: 'var(--space-4)', fontSize: 14, opacity: 0.8 }}>
          No pending brands. CSV rows enriched by AI and weekly discovery results land here.
        </div>
      )}

      {items.map(item => (
        <SuggestionCard
          key={item.id}
          item={item}
          busy={busyId === item.id}
          onApprove={() => handleApprove(item.id, item.name)}
          onEdit={() => setEditing(item)}
          onReject={() => handleReject(item.id, item.name)}
        />
      ))}

      {!loading && rejected.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          <button
            type="button"
            onClick={() => setRejectedOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-divider)', background: 'var(--color-surface)',
              cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 14,
              fontWeight: 600, color: 'var(--color-text)', textAlign: 'left',
            }}
          >
            {rejectedOpen
              ? <ChevronDown size={16} strokeWidth={STROKE} />
              : <ChevronRight size={16} strokeWidth={STROKE} />}
            Rejected ({rejected.length})
            <span style={{ fontWeight: 400, opacity: 0.6, fontSize: 13 }}>
              &mdash; restore to move back to review
            </span>
          </button>

          {rejectedOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {rejected.map(item => {
                const location = [item.hq_city, item.hq_state, item.country].filter(Boolean).join(', ');
                return (
                  <div
                    key={item.id}
                    className="card"
                    style={{
                      flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                      gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)',
                      flexWrap: 'wrap', opacity: 0.85,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <strong>{item.name}</strong>
                        {item.category && <span className="tag tag-neutral">{item.category}</span>}
                        {item.website && (
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          >
                            <Globe size={12} strokeWidth={STROKE} /> site
                          </a>
                        )}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 3 }}>
                        {location && <>{location} &middot; </>}
                        discovered {formatDate(item.discovered_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: 13, flex: 'none' }}
                      onClick={() => handleRestore(item.id, item.name)}
                      disabled={busyId === item.id}
                    >
                      {busyId === item.id
                        ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                        : <RotateCcw size={14} strokeWidth={STROKE} />}
                      Restore
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditPendingBrandModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setItems(prev => prev.map(p => (p.id === updated.id ? updated : p)));
            setEditing(null);
            setFlash({ kind: 'ok', text: `Updated ${updated.name}.` });
          }}
        />
      )}
    </OrganicShell>
  );
}

function SuggestionCard({ item, busy, onApprove, onEdit, onReject }) {
  const confidence = formatConfidence(item.confidence_score);
  const location = [item.hq_city, item.hq_state, item.country].filter(Boolean).join(', ');
  const isDuplicate = Boolean(item.already_tracked_brand_name);
  const missing = RESEARCHABLE.filter(([key]) => !item[key]).map(([, label]) => label);

  const facts = [];
  if (item.website) {
    facts.push([Globe, <a key="w" href={item.website} target="_blank" rel="noopener noreferrer">{item.website.replace(/^https?:\/\//, '')}</a>]);
  }
  if (item.instagram_handle) {
    facts.push([Instagram, <a key="i" href={`https://instagram.com/${item.instagram_handle}`} target="_blank" rel="noopener noreferrer">@{item.instagram_handle}</a>]);
  }
  if (item.tiktok_handle) {
    facts.push([Video, <a key="t" href={`https://tiktok.com/@${item.tiktok_handle}`} target="_blank" rel="noopener noreferrer">@{item.tiktok_handle}</a>]);
  }
  if (location) facts.push([MapPin, location]);
  if (item.founders) {
    const founders = Array.isArray(item.founder_linkedin_urls) && item.founder_linkedin_urls.length > 0
      ? item.founder_linkedin_urls
      : item.founders.split(',').map(n => ({ name: n.trim(), linkedin_url: null }));
    facts.push([Users, (
      <span key="f" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {founders.map((f, idx) => (
          <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {f.name}
            {f.linkedin_url && (
              <a href={f.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label={`${f.name} on LinkedIn`} style={{ display: 'inline-flex' }}>
                <Linkedin size={12} strokeWidth={STROKE} />
              </a>
            )}
            {idx < founders.length - 1 && ','}
          </span>
        ))}
      </span>
    )]);
  }
  if (item.founded_year) facts.push([Calendar, `Founded ${item.founded_year}`]);
  if (item.source && item.source !== 'discovery' && facts.length === 0) {
    facts.push([FileText, `${item.source} — name only`]);
  }

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 420px', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>{item.name}</h3>
            {item.category && <span className="tag tag-neutral">{item.category}</span>}
            {confidence != null && <span style={{ fontSize: 12, opacity: 0.65 }}>confidence {confidence}%</span>}
          </div>

          {confidence != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{ width: 120, height: 8, borderRadius: 999, background: 'var(--color-neutral-300)', overflow: 'hidden' }}>
                <div style={{ height: 8, borderRadius: 999, width: `${confidence}%`, background: confidenceColor(confidence) }} />
              </div>
            </div>
          )}

          {isDuplicate && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-accent-200)', fontSize: 13 }}>
              <AlertTriangle size={15} strokeWidth={STROKE} style={{ flex: 'none', marginTop: 2 }} />
              <span>
                Already tracked as{' '}
                <Link to={`/brands/${item.already_tracked_brand_id}`} style={{ fontWeight: 600 }}>
                  {item.already_tracked_brand_name}
                </Link>
                {' '}&mdash; approving marks this a duplicate without running research.
              </span>
            </div>
          )}

          {item.description && (
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8, textWrap: 'pretty' }}>{item.description}</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
            {facts.map(([Icon, value], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Icon size={15} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
                <span>{value}</span>
              </div>
            ))}
            {missing.length > 0 && !isDuplicate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', opacity: 0.6 }}>
                <CircleDashed size={15} strokeWidth={STROKE} style={{ flex: 'none' }} />
                <span>Research will fill: {missing.join(', ')}</span>
              </div>
            )}
            <span style={{ fontSize: 12, opacity: 0.55 }}>Discovered {formatDate(item.discovered_at)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 'none', minWidth: 180 }}>
          <button
            type="button"
            className={isDuplicate ? 'btn btn-secondary' : 'btn btn-primary'}
            onClick={onApprove}
            disabled={busy}
            title={isDuplicate
              ? `Already tracked as ${item.already_tracked_brand_name} — marks this suggestion as a duplicate without running research or billing a credit`
              : 'Approve and kick off deep research in the background (filled fields appear here when ready)'}
          >
            {busy
              ? <Loader2 size={16} strokeWidth={STROKE} className="spin" />
              : (isDuplicate ? <CheckCircle2 size={16} strokeWidth={STROKE} /> : <Sparkles size={16} strokeWidth={STROKE} />)}
            {busy ? 'Working…' : (isDuplicate ? 'Mark duplicate' : 'Approve & enrich')}
          </button>
          <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} onClick={onEdit} disabled={busy}>
            <Pencil size={15} strokeWidth={STROKE} /> Edit fields
          </button>
          <button type="button" className="btn btn-ghost" style={{ fontSize: 13 }} onClick={onReject} disabled={busy}>
            <XCircle size={15} strokeWidth={STROKE} /> Reject
          </button>
        </div>
      </div>
    </div>
  );
}

const ENRICHMENT_LABEL = {
  pending: 'Queued',
  running: 'Researching',
  completed: 'Done',
  merged: 'Merged',
  credits_denied: 'Skipped',
  provider_error: 'Failed',
  failed: 'Failed',
};

function EnrichmentBanners({ entries, onDismiss }) {
  const ids = Object.keys(entries || {});
  if (ids.length === 0) return null;
  return (
    <>
      {ids.map(id => {
        const entry = entries[id];
        const isTerminal = ENRICHMENT_TERMINAL.has(entry.status);
        const ok = entry.status === 'completed' || entry.status === 'merged';
        const filledSummary = describeFilled(entry.filled);
        const label = ENRICHMENT_LABEL[entry.status] || 'Queued';
        return (
          <div
            key={id}
            className="card"
            style={{
              flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
              gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)',
              background: isTerminal && !ok ? 'var(--color-accent-200)' : 'var(--color-accent-2-100)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
              {isTerminal
                ? (ok
                  ? <CheckCircle2 size={17} strokeWidth={STROKE} style={{ flex: 'none' }} />
                  : <XCircle size={17} strokeWidth={STROKE} style={{ flex: 'none' }} />)
                : <Loader2 size={17} strokeWidth={STROKE} className="spin" style={{ flex: 'none' }} />}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{label}: {entry.name}</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  {entry.status === 'completed' && filledSummary
                    ? `Deep research filled: ${filledSummary}.${entry.message ? ` ${entry.message}` : ''}`
                    : (entry.message || (
                      isTerminal
                        ? 'No new data.'
                        : 'Deep research running in the background — filled fields appear here when it finishes.'
                    ))}
                </div>
              </div>
            </div>
            {isTerminal ? (
              <button type="button" className="btn btn-icon btn-secondary" title="Dismiss" aria-label="Dismiss" onClick={() => onDismiss(id)}>
                <X size={15} strokeWidth={STROKE} />
              </button>
            ) : (
              <span style={{ fontSize: 12, opacity: 0.6, flex: 'none' }}>{startedAgo(entry.startedAt)}</span>
            )}
          </div>
        );
      })}
    </>
  );
}

function joinLinkedinUrls(item) {
  if (!Array.isArray(item.founder_linkedin_urls) || item.founder_linkedin_urls.length === 0) {
    return '';
  }
  return item.founder_linkedin_urls.map(f => f.linkedin_url || '').join(', ');
}

function EditPendingBrandModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item.name || '',
    category: item.category || '',
    description: item.description || '',
    website: item.website || '',
    instagram_handle: item.instagram_handle || '',
    tiktok_handle: item.tiktok_handle || '',
    country: item.country || '',
    hq_city: item.hq_city || '',
    hq_state: item.hq_state || '',
    founders: item.founders || '',
    founder_linkedin_urls: joinLinkedinUrls(item),
    founded_year: item.founded_year ? String(item.founded_year) : '',
    parent_company: item.parent_company || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload = { ...form };
    if (form.founded_year === '') {
      payload.founded_year = null;
    } else {
      const yr = Number(form.founded_year);
      if (Number.isFinite(yr)) {
        payload.founded_year = yr;
      } else {
        setError('Founded year must be a number.');
        setSaving(false);
        return;
      }
    }
    try {
      const updated = await api.updatePendingBrand(item.id, payload);
      onSaved(updated);
    } catch (err) {
      setError(err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 100%)', maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="dialog-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={18} strokeWidth={STROKE} /> Edit pending brand
          </div>
          <button type="button" className="btn btn-icon btn-secondary" aria-label="Close" onClick={onClose}>
            <X size={16} strokeWidth={STROKE} />
          </button>
        </div>

        {error && (
          <div style={{ background: 'var(--color-accent-200)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {error}
          </div>
        )}

        <Field label="Name">
          <input className="input" value={form.name} onChange={setField('name')} />
        </Field>
        <Field label="Category">
          <input className="input" value={form.category} onChange={setField('category')} />
        </Field>
        <Field label="Description">
          <textarea className="input" rows={3} value={form.description} onChange={setField('description')} />
        </Field>
        <Field label="Website">
          <input className="input" value={form.website} onChange={setField('website')} placeholder="https://" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Instagram handle">
            <input className="input" value={form.instagram_handle} onChange={setField('instagram_handle')} />
          </Field>
          <Field label="TikTok handle">
            <input className="input" value={form.tiktok_handle} onChange={setField('tiktok_handle')} />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Country">
            <input className="input" value={form.country} onChange={setField('country')} />
          </Field>
          <Field label="HQ city">
            <input className="input" value={form.hq_city} onChange={setField('hq_city')} />
          </Field>
          <Field label="HQ state">
            <input className="input" value={form.hq_state} onChange={setField('hq_state')} />
          </Field>
        </div>
        <Field label="Founders (comma-separated)">
          <input className="input" value={form.founders} onChange={setField('founders')} />
        </Field>
        <Field
          label="Founder LinkedIn URLs (comma-separated, aligned with founders)"
          hint="Use empty slots for founders without a profile, e.g. https://..., , https://..."
        >
          <input className="input" value={form.founder_linkedin_urls} onChange={setField('founder_linkedin_urls')} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
          <Field label="Founded year">
            <input className="input" type="number" value={form.founded_year} onChange={setField('founded_year')} />
          </Field>
          <Field label="Parent company">
            <input className="input" value={form.parent_company} onChange={setField('parent_company')} />
          </Field>
        </div>

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving
              ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
              : <CheckCircle2 size={14} strokeWidth={STROKE} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="field" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'color-mix(in srgb, var(--color-text) 70%, transparent)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11, opacity: 0.6 }}>{hint}</span>}
    </label>
  );
}

export default PendingReview;
